import type { AccountView, InvitationEmailOutcome } from "@kwp/contracts";
import type { MyWorkspace } from "../../lib/me";

/**
 * Which mailbox the invitation email starts from (WP-027): the admin's default From for the
 * current workspace when it is still connected, else their first account; null with none.
 */
export function defaultSender(accounts: readonly AccountView[], defaultFromAccountId: string | null): string | null {
  if (defaultFromAccountId !== null && accounts.some((a) => a.id === defaultFromAccountId)) return defaultFromAccountId;
  const first = accounts[0];
  return first === undefined ? null : first.id;
}

/** Which workspaces an admin can invite people into (WP-028): shared ones they own, never Personal. */
export function invitableWorkspaces(workspaces: readonly MyWorkspace[]): MyWorkspace[] {
  return workspaces.filter((w) => w.role === "owner" && w.name !== "Personal");
}

/** The workspace the invite form starts on: the current one when it is invitable, else the first. */
export function defaultInviteWorkspace(invitable: readonly MyWorkspace[], currentId: string | null): string | null {
  if (currentId !== null && invitable.some((w) => w.id === currentId)) return currentId;
  const first = invitable[0];
  return first === undefined ? null : first.id;
}

/** What an invite did, for the outcome line; null after a resend. */
export interface InviteResult {
  workspaceName: string;
  /** They had already signed in and were added to the workspace at once. */
  added: boolean;
}

/** What to tell the admin after an invite or a resend; `tone: "bad"` offers "Resend invitation". */
export function outcomeMessage(email: string, outcome: InvitationEmailOutcome, invited: InviteResult | null = null): { tone: "ok" | "approval" | "bad"; text: string } {
  const lead = invited === null ? "" : invited.added ? `Added ${email} to ${invited.workspaceName}: they have already signed in.` : `Invited ${email} to ${invited.workspaceName}.`;
  const join = (s: string) => (lead === "" ? s : `${lead} ${s}`);
  const added = invited !== null && invited.added;
  switch (outcome.status) {
    case "not_requested":
      return { tone: "ok", text: lead };
    case "sent":
      return { tone: "ok", text: join(added ? `Emailed ${email} from ${outcome.from} that they were added.` : `Emailed the invitation to ${email} from ${outcome.from}.`) };
    case "awaiting_approval":
      return { tone: "approval", text: join("Emailing the invitation needs your approval.") };
    case "failed":
      return { tone: "bad", text: join(`The invitation email to ${email} failed: ${outcome.reason}`) };
  }
}
